import { useCallback, useRef, useState } from 'react';
import { API_BASE } from '../config';
import './EvaluatorGrade.css';

export const GRADE_OPTIONS = [
    { value: '상', label: '우수', icon: '🥇', tone: 'excellent' },
    { value: '중', label: '양호', icon: '🥈', tone: 'good' },
    { value: '하', label: '미흡', icon: '⚠️', tone: 'needs-work' },
];
export const gradeLabel = value => {
    const grade = GRADE_OPTIONS.find(option => option.value === value);
    return grade ? `${grade.icon} ${grade.label}` : value === '지인' ? '미지정' : value || '미지정';
};
const keyOf = user => String(user.user_id);
const gradeFields = value => ({ grade: value.grade === '지인' ? null : value.grade ?? null, grade_version: Number(value.grade_version) || 0 });

export function matchesGrade(user, filter) {
    if (Array.isArray(filter)) return !filter.length || filter.some(value => matchesGrade(user, value));
    return !filter || (filter === '미지정' ? (user.grade == null || user.grade === '지인') : user.grade === filter);
}

// Retain the highest observed version across reads and business changes. A read
// begun before a save (including one arriving after a newer read) stays harmless.
export function useEvaluatorGrades(onChange) {
    const latest = useRef(new Map());
    const pending = useRef(new Set());
    const change = useRef(onChange);
    change.current = onChange;
    const [notices, setNotices] = useState({});
    const mergeGrades = useCallback(users => users.map(user => {
        const key = keyOf(user), incoming = gradeFields(user), known = latest.current.get(key);
        if (known && known.grade_version > incoming.grade_version) return { ...user, ...known };
        latest.current.set(key, incoming);
        return { ...user, ...incoming };
    }), []);

    const saveGrade = async (user, grade) => {
        const key = keyOf(user);
        if (pending.current.has(key)) return;
        const previous = latest.current.get(key) || gradeFields(user);
        if (previous.grade === grade) return;
        pending.current.add(key);
        setNotices(previousNotices => ({ ...previousNotices, [key]: { name: user.name, saving: true, grade, message: '저장 중…' } }));
        try {
            const response = await fetch(`${API_BASE}/api/admin/users/${user.user_id}/grade`, {
                method: 'PUT', headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ grade, expected_version: previous.grade_version }),
            });
            const result = await response.json().catch(() => ({}));
            if (response.ok || (response.status === 409 && result.current)) {
                const saved = gradeFields(response.ok ? result : result.current);
                const known = latest.current.get(key);
                const current = known && known.grade_version > saved.grade_version ? known : saved;
                latest.current.set(key, current);
                change.current(user.user_id, current);
                setNotices(previousNotices => ({ ...previousNotices, [key]: { name: user.name, error: !response.ok,
                    message: response.ok ? '저장 완료' : '다른 관리자가 등급을 변경했습니다. 최신 등급을 확인하고 다시 선택해 주세요.' } }));
            } else {
                throw new Error(result.error || '등급을 저장하지 못했습니다. 다시 선택해 주세요.');
            }
        } catch (error) {
            setNotices(previousNotices => ({ ...previousNotices, [key]: { name: user.name, error: true,
                message: error.message === 'Failed to fetch' || error.message === 'offline' ? '등급을 저장하지 못했습니다. 연결을 확인하고 다시 선택해 주세요.' : error.message } }));
        } finally { pending.current.delete(key); }
    };
    return { mergeGrades, saveGrade, notices };
}

export function GradeFilter({ value, onChange, id, disabled = false }) {
    return <label className="evaluator-grade-filter" htmlFor={id}>평가자 등급
        <select id={id} aria-label="평가자 등급 필터" value={value} disabled={disabled} onChange={event => onChange(event.target.value)}>
            <option value="">전체</option><option value="미지정">미지정</option>{GRADE_OPTIONS.map(grade => <option key={grade.value} value={grade.value}>{gradeLabel(grade.value)}</option>)}
        </select>
    </label>;
}

export function GradeSelect({ user, controller, id, idPrefix = 'evaluator-grade' }) {
    const notice = controller.notices[keyOf(user)];
    const noticeId = `${idPrefix}-notice-${user.user_id}`;
    const selectedValue = (notice?.saving ? notice.grade : user.grade) ?? '';
    const selected = selectedValue === '지인' ? '' : selectedValue;
    const tone = GRADE_OPTIONS.find(grade => grade.value === selected)?.tone || 'unset';
    return <div className="evaluator-grade">
        <select id={id} aria-label={`${user.name} 평가자 등급`} aria-describedby={notice?.message ? noticeId : undefined}
            className={`evaluator-grade-select is-${tone}`} title={gradeLabel(selected)}
            value={selected} disabled={!!notice?.saving}
            onChange={event => controller.saveGrade(user, event.target.value || null)}>
            <option value="">미지정</option>{GRADE_OPTIONS.map(grade => <option key={grade.value} value={grade.value}>{gradeLabel(grade.value)}</option>)}
        </select>
        {notice?.message && <span id={noticeId} className={notice.error ? 'evaluator-grade-error' : 'evaluator-grade-notice'} role={notice.error ? 'alert' : 'status'}>{notice.message}</span>}
    </div>;
}

export function HiddenGradeNotices({ controller, visibleUsers }) {
    const visible = new Set(visibleUsers.map(keyOf));
    return Object.entries(controller.notices).filter(([key, notice]) => !visible.has(key) && notice.message).map(([key, notice]) =>
        <p key={key} className={notice.error ? 'evaluator-grade-error' : 'evaluator-grade-notice'} role={notice.error ? 'alert' : 'status'}>{notice.name} 등급: {notice.message}</p>);
}

export function GradeButtons({ value = [], onChange, label = '평가자 등급 필터', disabled = false, className = '', showIcons = true }) {
    const selected = Array.isArray(value) ? value : value ? [value] : [];
    return <div className={`dashboard-filter-row ${className}`} role="group" aria-label={label}>
        <span>등급</span><button type="button" disabled={disabled} aria-pressed={!selected.length} onClick={() => onChange([])}>전체</button>
        {['미지정', ...GRADE_OPTIONS.map(grade => grade.value)].map(grade => <button type="button" key={grade} className={`is-${GRADE_OPTIONS.find(option => option.value === grade)?.tone || 'unset'}`} disabled={disabled} aria-pressed={selected.includes(grade)} onClick={() => onChange(selected.includes(grade) ? selected.filter(value => value !== grade) : [...selected, grade])}>{showIcons ? gradeLabel(grade) : GRADE_OPTIONS.find(option => option.value === grade)?.label || grade}</button>)}
    </div>;
}
